// src/components/Auth/LogoutComponent.tsx
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, fetchUserAttributes } from 'aws-amplify/auth';
import { useUserContext } from '../Context/UserContext';

export default function LogoutComponent() {
  const navigate = useNavigate();
  const { setFirstName, setEmail } = useUserContext();

  // ───────────────────────── DEBUG HELPERS ─────────────────────────
  function snapshotLS(label: string) {
    try {
      const keys = Object.keys(localStorage);
      const interesting = [
        'dev_access_granted',
        'email',
        'dfuse:lastIssueId',
        ...keys.filter((k) => k.startsWith('solutionsSaved:')).slice(0, 5),
      ];
      const preview: Record<string, string | null> = {};
      for (const k of interesting) preview[k] = localStorage.getItem(k);
      console.groupCollapsed(`[Logout][LS Snapshot] ${label}`);
      console.log('Count:', keys.length);
      console.log('Keys:', keys);
      console.log('Preview:', preview);
      console.groupEnd();
    } catch (e) {
      console.warn('[Logout] snapshotLS failed:', e);
    }
  }

  function snapshotIDB(label: string) {
    console.groupCollapsed(`[Logout][IDB] ${label}`);
    console.log('navigator.storage:', (navigator as any).storage);
    console.groupEnd();
  }

  async function listServiceWorkerState(label: string) {
    console.groupCollapsed(`[Logout][SW] ${label}`);
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        console.log('Registrations:', regs.map((r) => ({
          scope: r.scope,
          active: !!r.active,
          installing: !!r.installing,
          waiting: !!r.waiting,
        })));
      } else {
        console.log('Service workers not supported');
      }
    } catch (e) {
      console.warn('List SW failed', e);
    }
    try {
      if ('caches' in window) {
        const names = await caches.keys();
        console.log('Cache names:', names);
      } else {
        console.log('CacheStorage not supported');
      }
    } catch (e) {
      console.warn('List caches failed', e);
    }
    console.groupEnd();
  }

  /** Watch for any repopulation after cleanup (logs callsites). */
  function startLocalStorageWriteWatch() {
    try {
      const _set = localStorage.setItem.bind(localStorage);
      const _remove = localStorage.removeItem.bind(localStorage);
      const _clear = localStorage.clear.bind(localStorage);
      (localStorage as any).__watching = true;

      localStorage.setItem = function (k: string, v: string) {
        console.groupCollapsed('%c[Logout][LS Watch] setItem', 'color:#0a0', k, v);
        console.trace();
        console.groupEnd();
        return _set(k, v);
      } as any;

      localStorage.removeItem = function (k: string) {
        console.groupCollapsed('%c[Logout][LS Watch] removeItem', 'color:#a60', k);
        console.trace();
        console.groupEnd();
        return _remove(k);
      } as any;

      localStorage.clear = function () {
        console.groupCollapsed('%c[Logout][LS Watch] clear', 'color:#a00');
        console.trace();
        console.groupEnd();
        return _clear();
      } as any;

      return () => {
        try {
          localStorage.setItem = _set as any;
          localStorage.removeItem = _remove as any;
          localStorage.clear = _clear as any;
          delete (localStorage as any).__watching;
        } catch {}
      };
    } catch {
      return () => {};
    }
  }

  // ───────────────────────── CLEANERS ─────────────────────────
  function clearLocalStoragePreserveDevAccess() {
    console.groupCollapsed('[Logout] clearLocalStoragePreserveDevAccess');
    try {
      const preserved = localStorage.getItem('dev_access_granted');
      snapshotLS('BEFORE localStorage.clear()');
      localStorage.clear();
      console.log('localStorage cleared.');
      if (preserved !== null) {
        localStorage.setItem('dev_access_granted', preserved);
        console.log('Restored dev_access_granted');
      } else {
        console.log('No dev_access_granted to restore');
      }
      snapshotLS('AFTER localStorage.clear() + restore');
    } catch (e) {
      console.warn('clearLocalStoragePreserveDevAccess failed:', e);
    }
    console.groupEnd();
  }

  function clearSessionStorageAll() {
    console.groupCollapsed('[Logout] clearSessionStorageAll');
    try {
      sessionStorage.clear();
      console.log('sessionStorage cleared');
    } catch (e) {
      console.warn('sessionStorage.clear failed:', e);
    }
    console.groupEnd();
  }

  async function clearIndexedDBHeuristics() {
    console.groupCollapsed('[Logout] clearIndexedDBHeuristics');
    snapshotIDB('BEFORE');
    try {
      const dbs = await (indexedDB as any).databases?.();
      console.log('indexedDB.databases():', dbs);
      if (Array.isArray(dbs)) {
        for (const db of dbs) {
          if (!db?.name) continue;
          console.log('Deleting DB:', db.name);
          await new Promise<void>((resolve) => {
            const req = indexedDB.deleteDatabase(db.name!);
            req.onsuccess = req.onerror = req.onblocked = () => resolve();
          });
        }
        snapshotIDB('AFTER delete (databases API path)');
        console.groupEnd();
        return;
      }
    } catch (e) {
      console.warn('databases() path failed, falling back:', e);
    }

    const candidates = [
      'AmplifyDataStore',
      'amplify-datastore',
      'aws-amplify-cache',
      'aws-websocket-manager',
    ];
    for (const name of candidates) {
      try {
        console.log('Deleting fallback DB:', name);
        await new Promise<void>((resolve) => {
          const req = indexedDB.deleteDatabase(name);
          req.onsuccess = req.onerror = req.onblocked = () => resolve();
        });
      } catch (e) {
        console.warn('deleteDatabase fallback failed:', name, e);
      }
    }
    snapshotIDB('AFTER delete (fallback path)');
    console.groupEnd();
  }

  async function clearServiceWorkerCaches() {
    console.groupCollapsed('[Logout] clearServiceWorkerCaches');
    await listServiceWorkerState('BEFORE');
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => {
          console.log('Unregistering SW:', r.scope);
          return r.unregister();
        }));
      }
    } catch (e) {
      console.warn('SW unregister failed:', e);
    }

    try {
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => {
          console.log('Deleting cache:', n);
          return caches.delete(n);
        }));
      }
    } catch (e) {
      console.warn('Cache delete failed:', e);
    }
    await listServiceWorkerState('AFTER');
    console.groupEnd();
  }

  useEffect(() => {
    (async () => {
      console.group('[Logout] EFFECT start');
      snapshotLS('ON ENTER');

      // Watch for anything that writes after cleanup
      const stopWatch = startLocalStorageWriteWatch();

      try {
        // Sign out if currently signed in
        try {
          console.log('[Logout] fetchUserAttributes → checking sign-in');
          await fetchUserAttributes(); // throws if not signed in
          console.log('[Logout] User is signed in → signOut({ global: true })');
          await signOut({ global: true });
          console.log('[Logout] signOut complete');
        } catch (e) {
          console.log('[Logout] Not signed in or fetchUserAttributes failed:', e);
        }

        // Clear app user context
        console.log('[Logout] Clearing React user context');
        setFirstName(null);
        setEmail(null);

        // Storage cleanup order
        clearLocalStoragePreserveDevAccess();
        clearSessionStorageAll();
        await clearIndexedDBHeuristics();
        await clearServiceWorkerCaches();

        // Extra: ensure all Cognito keys are gone (after signOut)
        try {
          const keys = Object.keys(localStorage);
          for (const k of keys) {
            if (k.startsWith('CognitoIdentityServiceProvider.')) {
              console.log('[Logout] Removing residual Cognito key:', k);
              localStorage.removeItem(k);
            }
          }
        } catch (e) {
          console.warn('[Logout] Sweep Cognito keys failed:', e);
        }

        // Final snapshot before navigate
        snapshotLS('BEFORE NAVIGATE');
      } finally {
        // Small delay so logs/stack traces flush if something writes immediately
        await new Promise((r) => setTimeout(r, 100));
        console.groupEnd();
        stopWatch();
        // TIP: redirect to a sterile page if Home repopulates keys
        navigate('/goodbye', { replace: true }); // or '/' if you prefer
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
